import {
  Material,
  Quote,
  SimpleService,
  ProductTemplate,
  Client,
  Supplier,
  WorkOrder,
  Receipt,
  Appointment,
  StaffMember,
  SubscriptionClubPlan,
  StorefrontProfile,
  InventoryBatch,
  StockMovement,
  ClinicalPatient,
  ClinicalConsultation,
  ClinicalVaccination,
  ClinicalExamVault,
  AccountantAccess
} from '../../types';
import { getLocalDateString } from '../../modules/erp/utils/calendarUtils';

export function mapSupabaseMaterial(m: any): Material {
  const meta = m.niche_metadata && typeof m.niche_metadata === 'object' ? m.niche_metadata : undefined;
  return {
    id: m.id,
    userId: m.user_id,
    name: m.name,
    brand: m.brand || '',
    supplierId: m.supplier_id || meta?.supplierId || undefined,
    supplierName: m.supplier_name || meta?.supplierName || undefined,
    stockControlMode: m.stock_control_mode || meta?.stockControlMode || undefined,
    packQuantity: m.pack_quantity != null ? Number(m.pack_quantity) : (meta?.packQuantity != null ? Number(meta.packQuantity) : undefined),
    category: m.category,
    unit: m.unit,
    pricePaid: Number(m.price || 0),
    yieldAmount: Number(m.yield || 1),
    depreciationMonths: m.depreciation_months ?? undefined,
    imageUrl: m.image_url || undefined,
    itemType: m.item_type || (m.category === 'fixo' ? 'custo_fixo' : ((m.sale_price && Number(m.sale_price) > 0) ? 'produto_venda' : 'insumo')),
    salePrice: m.sale_price != null ? Number(m.sale_price) : undefined,
    marginPercent: m.margin_percent != null ? Number(m.margin_percent) : undefined,
    stockQuantity: m.stock_quantity != null ? Number(m.stock_quantity) : (m.stockquantity != null ? Number(m.stockquantity) : undefined),
    barcode: m.barcode_ean || m.barcode || undefined,
    sku: m.sku || undefined,
    ncm: m.ncm || undefined,
    cest: m.cest || undefined,
    originCode: m.origin_code != null ? Number(m.origin_code) : undefined,
    minStock: m.min_stock != null ? Number(m.min_stock) : undefined,
    location: m.location || undefined,
    weightKg: m.weight_kg != null ? Number(m.weight_kg) : undefined,
    dimensionsCm: m.dimensions_cm || undefined,
    nicheCategory: m.niche_category || undefined,
    nicheMetadata: meta
  };
}

export function mapSupabaseQuote(q: any): Quote {
  return {
    id: q.id,
    userId: q.user_id,
    clientName: q.client_name,
    rawServiceName: q.service_name,
    category: q.category,
    materialCost: q.material_cost,
    timeSpentMinutes: q.time_spent_minutes,
    appliedProfitMargin: q.applied_profit_margin,
    finalSuggestedPrice: q.final_price,
    finalPrice: q.final_price,
    final_price: q.final_price,
    material_cost: q.material_cost,
    status: q.status,
    createdAt: q.created_at,
    closedAt: q.closed_at,
    explanation: q.explanation,
    statusHistory: Array.isArray(q.status_history) ? q.status_history : [],
    products: (Array.isArray(q.products) ? q.products : []).map((p: any) => ({
      name: String(p.name || ''),
      unitValue: Number(p.unitValue || p.unit_value || 0),
      quantity: Number(p.quantity || 0),
      total: Number(p.total || 0),
      isPassThrough: Boolean(p.isPassThrough ?? p.is_pass_through ?? false),
      materialId: p.materialId || p.material_id || undefined,
      snapshotPrice: p.snapshotPrice !== undefined ? Number(p.snapshotPrice) : undefined,
      snapshotAt: p.snapshotAt || undefined,
      isMarkupHidden: p.isMarkupHidden !== undefined ? Boolean(p.isMarkupHidden) : undefined,
      isFromRecipe: p.isFromRecipe !== undefined ? Boolean(p.isFromRecipe) : undefined,
      showOnPdf: p.showOnPdf !== undefined ? Boolean(p.showOnPdf) : undefined
    })),
    services: (Array.isArray(q.services) ? q.services : []).map((s: any) => ({
      name: String(s.name || ''),
      value: Number(s.value || 0)
    }))
  };
}

export function mapSupabaseService(s: any): SimpleService {
  return {
    id: s.id,
    userId: s.user_id,
    name: s.name,
    value: s.value,
    type: s.type === 'hourly' ? 'hourly' : 'fixed',
    description: s.description,
    promo: s.promo_config || s.promo || undefined,
    allowedStaffIds: Array.isArray(s.allowed_staff_ids)
      ? s.allowed_staff_ids
      : (Array.isArray(s.promo_config?.allowedStaffIds) ? s.promo_config.allowedStaffIds : undefined),
    createdAt: s.created_at
  };
}

export function mapSupabaseRecipe(r: any): ProductTemplate {
  return {
    id: r.id,
    userId: r.user_id,
    name: r.name,
    executionTimeMinutes: r.execution_time_minutes,
    createdAt: r.created_at,
    items: (r.template_items || []).map((ti: any) => ({
      id: ti.id,
      templateId: ti.template_id,
      materialId: ti.material_id,
      quantity: ti.quantity
    }))
  };
}

export function mapSupabaseClient(c: any): Client {
  return {
    id: c.id,
    userId: c.user_id,
    name: c.name,
    phone: c.phone || undefined,
    email: c.email || undefined,
    document: c.document || undefined,
    address: c.address || undefined,
    tags: c.tags || [],
    totalSpent: Number(c.total_spent || 0),
    ordersCount: Number(c.orders_count || 0),
    source: c.source || 'manual',
    notes: c.notes || undefined,
    vehicleData: c.vehicle_data || undefined,
    vehicleHistory: c.vehicle_history || undefined,
    beautyCard: c.beauty_card || undefined,
    companyData: c.company_data || undefined,
    lastInteractionAt: c.last_interaction_at || undefined,
    subscriptionPlanId: c.subscription_plan_id || undefined,
    subscriptionPlanName: c.subscription_plan_name || undefined,
    subscriptionStatus: c.subscription_status || undefined,
    loyaltyPoints: c.loyalty_points || 0,
    createdAt: c.created_at,
    updatedAt: c.updated_at
  };
}

export function mapSupabaseSupplier(s: any): Supplier {
  return {
    id: s.id,
    userId: s.user_id,
    name: s.name,
    tradeName: s.trade_name || undefined,
    cnpj: s.cnpj || undefined,
    ie: s.ie || undefined,
    cnae: s.cnae || undefined,
    category: s.category || 'Geral & Outros',
    phone: s.phone || undefined,
    email: s.email || undefined,
    address: s.address || undefined,
    paymentTerms: s.payment_terms || undefined,
    rating: s.rating || 5,
    notes: s.notes || undefined,
    source: s.source || 'manual',
    suppliedProductsCount: Number(s.supplied_products_count || 0),
    totalPurchased: Number(s.total_purchased || 0),
    createdAt: s.created_at,
    updatedAt: s.updated_at
  };
}

export function mapSupabaseWorkOrder(w: any): WorkOrder {
  return {
    id: w.id,
    userId: w.user_id,
    quoteId: w.quote_id || undefined,
    orderNumber: w.order_number,
    title: w.title,
    nicheKey: w.niche_key || 'geral',
    status: w.status || 'open',
    clientName: w.client_name,
    clientPhone: w.client_phone || undefined,
    clientAddress: w.client_address || undefined,
    clientDocument: w.client_document || undefined,
    scheduledStart: w.scheduled_start || undefined,
    scheduledEnd: w.scheduled_end || undefined,
    completedAt: w.completed_at || undefined,
    checklistData: Array.isArray(w.checklist_data) ? w.checklist_data : [],
    itemsBreakdown: Array.isArray(w.items_breakdown) ? w.items_breakdown : [],
    technicalReport: w.technical_report || undefined,
    warrantyTerms: w.warranty_terms || undefined,
    totalLabor: Number(w.total_labor || 0),
    totalMaterials: Number(w.total_materials || 0),
    finalPrice: Number(w.final_price || 0),
    paymentTerms: w.payment_terms || undefined,
    notes: w.notes || undefined,
    vehicleData: w.vehicle_data || undefined,
    deviceData: w.device_data || undefined,
    hvacData: w.hvac_data || undefined,
    beautyData: w.beauty_data || undefined,
    laserData: w.laser_data || undefined,
    photos: Array.isArray(w.photos) ? w.photos : [],
    signatures: Array.isArray(w.signatures) ? w.signatures : [],
    createdAt: w.created_at,
    updatedAt: w.updated_at
  };
}

export function mapSupabaseReceipt(r: any): Receipt {
  return {
    id: r.id,
    userId: r.user_id,
    quoteId: r.quote_id || undefined,
    workOrderId: r.work_order_id || undefined,
    receiptNumber: r.receipt_number,
    totalServiceValue: Number(r.total_service_value || 0),
    amountPaid: Number(r.amount_paid || 0),
    remainingBalance: Number(r.remaining_balance || 0),
    paymentMethod: r.payment_method || 'pix',
    receiptType: r.receipt_type || 'full',
    installmentNumber: r.installment_number || 1,
    totalInstallments: r.total_installments || 1,
    clientName: r.client_name,
    clientDocument: r.client_document || undefined,
    description: r.description,
    pixQrCodeBase64: r.pix_qr_code_base64 || undefined,
    pixCopiaCola: r.pix_copia_cola || undefined,
    warrantyPeriod: r.warranty_period ?? undefined,
    warrantyUnit: r.warranty_unit || 'dias',
    warrantyTerms: r.warranty_terms || undefined,
    status: r.status || 'active',
    canceledAt: r.canceled_at || undefined,
    cancelReason: r.cancel_reason || undefined,
    issuedAt: r.issued_at || r.created_at,
    createdAt: r.created_at
  };
}

export function mapSupabaseAppointment(a: any): Appointment {
  return {
    id: a.id,
    userId: a.user_id,
    clientName: a.client_name,
    clientPhone: a.client_phone || undefined,
    clientAddress: a.client_address || undefined,
    serviceName: a.service_name,
    serviceId: a.service_id || undefined,
    servicesList: a.services_list || undefined,
    durationMinutes: a.duration_minutes || 60,
    scheduledDate: a.scheduled_date,
    scheduledTime: a.scheduled_time ? a.scheduled_time.slice(0, 5) : '09:00',
    price: Number(a.price || 0),
    status: a.status || 'confirmed',
    paymentStatus: a.payment_status || (a.status === 'completed' ? 'paid' : 'pending'),
    paymentMethod: a.payment_method || undefined,
    executedAt: a.executed_at || undefined,
    paidAt: a.paid_at || undefined,
    createdByRole: a.created_by_role || 'admin',
    packageTotalSessions: a.package_total_sessions || 1,
    packageCurrentSession: a.package_current_session || 1,
    staffMemberId: a.staff_member_id || undefined,
    staffMemberName: a.staff_member_name || undefined,
    commissionPercent: (a.commission_percent !== undefined && a.commission_percent !== null) ? Number(a.commission_percent) : 50,
    isRecurring: Boolean(a.is_recurring),
    recurringRule: a.recurring_rule || undefined,
    recurringGroupId: a.recurring_group_id || undefined,
    allDay: Boolean(a.all_day || (a.duration_minutes && a.duration_minutes >= 720)),
    notes: a.notes || undefined,
    reminderSent: Boolean(a.reminder_sent),
    historyTimeline: Array.isArray(a.history_timeline) ? a.history_timeline : [],
    createdAt: a.created_at,
    updatedAt: a.updated_at
  };
}

export function mapSupabaseStaffMember(s: any): StaffMember {
  const extractedServiceIds = Array.isArray(s.service_ids)
    ? s.service_ids
    : (Array.isArray(s.permissions?.serviceIds) ? s.permissions.serviceIds : undefined);

  const rawComm = (s.commission_percent !== undefined && s.commission_percent !== null) ? Number(s.commission_percent) : 50;
  const compType = s.compensation_type || (rawComm === 0 ? 'owner_revenue' : 'commission');

  return {
    id: s.id,
    userId: s.user_id,
    name: s.name,
    role: s.role,
    compensationType: compType,
    color: s.color || 'emerald',
    commissionPercent: rawComm,
    phone: s.phone || undefined,
    accessToken: s.access_token || undefined,
    pinCode: s.pin_code || undefined,
    permissions: s.permissions || {
      canCreateAppointments: true,
      canBlockSlots: true,
      canComplete: true,
      showClientPhone: true,
      showCommission: true
    },
    avatarUrl: s.avatar_url || undefined,
    serviceIds: extractedServiceIds && extractedServiceIds.length > 0 ? extractedServiceIds : undefined,
    active: Boolean(s.active),
    createdAt: s.created_at,
    updatedAt: s.updated_at
  };
}

export function mapSupabaseSubscriptionClub(c: any): SubscriptionClubPlan {
  return {
    id: c.id,
    userId: c.user_id,
    name: c.name,
    priceMonthly: Number(c.price_monthly || 0),
    description: c.description || '',
    benefits: Array.isArray(c.benefits) ? c.benefits : [],
    maxServicesPerMonth: c.max_services_per_month ?? undefined,
    eligibleServices: Array.isArray(c.eligible_services) ? c.eligible_services : [],
    activeSubscribersCount: Number(c.active_subscribers_count || 0),
    createdAt: c.created_at,
    updatedAt: c.updated_at
  };
}

export function mapSupabaseBatch(b: any): InventoryBatch {
  return {
    id: b.id,
    userId: b.user_id,
    materialId: b.material_id,
    materialName: b.material_name || undefined,
    batchCode: b.batch_code,
    expirationDate: b.expiration_date,
    costPrice: Number(b.cost_price || 0),
    initialQuantity: Number(b.initial_quantity || 0),
    currentQuantity: Number(b.current_quantity || 0),
    createdAt: b.created_at,
    updatedAt: b.updated_at
  };
}

export function mapSupabaseStockMovement(m: any): StockMovement {
  return {
    id: m.id,
    userId: m.user_id,
    materialId: m.material_id,
    materialName: m.material_name || undefined,
    batchId: m.batch_id || undefined,
    batchCode: m.batch_code || undefined,
    type: (m.movement_type || 'entry_adjustment') as any,
    quantity: Number(m.quantity || 0),
    unitCost: Number(m.unit_cost || 0),
    totalCost: Number(m.total_cost || 0),
    balanceAfter: Number(m.balance_after || 0),
    reasonNotes: m.reason || m.notes || undefined,
    referenceId: m.reference_id || undefined,
    createdAt: m.created_at
  };
}

export function mapSupabasePatient(p: any): ClinicalPatient {
  return {
    id: p.id,
    userId: p.user_id,
    clientId: p.client_id,
    clientName: p.client_name || undefined,
    clientPhone: p.client_phone || undefined,
    kind: p.patient_kind || 'pet',
    name: p.name,
    species: p.species || 'Canina',
    breed: p.breed || 'SRD',
    gender: p.gender || 'M',
    isNeutered: Boolean(p.is_neutered),
    birthDate: p.birth_date || undefined,
    bloodType: p.blood_type || undefined,
    microchipNumber: p.microchip_number || undefined,
    coatColor: p.coat_color || undefined,
    knownAllergies: p.known_allergies || undefined,
    chronicConditions: p.chronic_conditions || undefined,
    emergencyContact: p.emergency_contact || undefined,
    active: p.active !== false,
    createdAt: p.created_at,
    updatedAt: p.updated_at
  };
}

export function mapSupabaseConsultation(c: any): ClinicalConsultation {
  return {
    id: c.id,
    userId: c.user_id,
    patientId: c.patient_id,
    patientName: c.patient_name || undefined,
    appointmentId: c.appointment_id || undefined,
    staffMemberId: c.staff_member_id || undefined,
    staffMemberName: c.staff_member_name || undefined,
    subjectiveAnamnesis: c.subjective || c.subjective_anamnesis || '',
    objectiveExam: c.objective || c.objective_exam || {
      temperatureCelsius: 38.5,
      weightKg: 10,
      heartRateBpm: 90,
      respiratoryRateRpm: 24,
      hydrationLevel: 'Normal (<5%)',
      bodyConditionScore: '3/5 - Ideal',
      mucousMembraneColor: 'Normocoradas (Rosa)',
      capillaryRefillTimeSec: 2
    },
    assessmentDiagnosis: c.assessment || c.assessment_diagnosis || undefined,
    planTreatment: c.plan || c.plan_treatment || '',
    prescriptions: Array.isArray(c.prescriptions) ? c.prescriptions : [],
    consumedMaterials: Array.isArray(c.consumed_materials) ? c.consumed_materials : [],
    consultationFee: Number(c.consultation_fee || 0),
    materialsFee: Number(c.materials_fee || 0),
    totalAmount: Number(c.total_amount || 0),
    status: c.status || 'in_progress',
    finalizedAt: c.finalized_at || undefined,
    professionalSignatureHash: c.security_hash || c.professional_signature_hash || undefined,
    createdAt: c.created_at,
    updatedAt: c.updated_at || c.created_at
  };
}

export function mapSupabaseVaccination(v: any): ClinicalVaccination {
  return {
    id: v.id,
    userId: v.user_id,
    patientId: v.patient_id,
    patientName: v.patient_name || undefined,
    tutorName: v.tutor_name || undefined,
    tutorPhone: v.tutor_phone || undefined,
    consultationId: v.consultation_id || undefined,
    materialId: v.material_id || undefined,
    batchId: v.batch_id || undefined,
    vaccineName: v.vaccine_name,
    appliedAt: v.applied_date || v.applied_at || getLocalDateString(),
    nextDueDate: v.next_due_date,
    reminderCount: Number(v.reminder_count || 0),
    lastRemindedAt: v.last_reminded_at || undefined,
    status: v.status || 'pending',
    createdAt: v.created_at
  };
}

export function mapSupabaseExam(e: any): ClinicalExamVault {
  return {
    id: e.id,
    userId: e.user_id,
    patientId: e.patient_id,
    patientName: e.patient_name || undefined,
    consultationId: e.consultation_id || undefined,
    title: e.title,
    examType: e.exam_type || 'outro',
    reportPdfUrl: e.file_url || e.report_pdf_url || undefined,
    previewImageUrl: e.preview_url || e.preview_image_url || undefined,
    rawDicomR2Path: e.raw_dicom_url || e.raw_dicom_r2_path || undefined,
    rawFileSizeBytes: Number(e.raw_file_size_bytes || 0),
    dicomMetadata: e.dicom_metadata || undefined,
    legalRetentionUntil: e.legal_retention_date || e.legal_retention_until || getLocalDateString(new Date(Date.now() + 5 * 365 * 24 * 3600 * 1000)),
    createdAt: e.created_at
  };
}

export function mapSupabaseAccountantAccess(a: any): AccountantAccess {
  return {
    id: a.id,
    workspaceOwnerId: a.workspace_owner_id,
    accountantUserId: a.accountant_user_id || undefined,
    accountantEmail: a.accountant_email,
    accountantName: a.accountant_name || undefined,
    accountingFirm: a.accounting_firm || undefined,
    crcNumber: a.crc_number || undefined,
    status: a.status || 'pending',
    invitedAt: a.invited_at,
    acceptedAt: a.accepted_at || undefined,
    revokedAt: a.revoked_at || undefined,
    lastAccessedAt: a.last_accessed_at || undefined,
    permissions: a.permissions || undefined,
    createdAt: a.created_at
  };
}
